import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CreditCard, Truck, MapPin, Check } from 'lucide-react';
import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Card, CardContent, CardHeader, CardTitle } from '@kamalkari/ui';
import { formatPrice, US_STATES } from '@kamalkari/utils';
import { useCartStore } from '@/stores/cartStore';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/checkout')({
  component: CheckoutPage,
});

interface ShippingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const shippingOptions = [
  { id: 'usps_priority', name: 'USPS Priority Mail', price: 899, days: '2-3' },
  { id: 'ups_ground', name: 'UPS Ground', price: 1299, days: '5-7' },
  { id: 'fedex_express', name: 'FedEx Express', price: 2499, days: '1-2' },
  { id: 'dhl_express', name: 'DHL Express (International)', price: 4999, days: '3-5' },
];

function CheckoutPage() {
  const navigate = useNavigate();
  const { items, getTotalPrice, clearCart } = useCartStore();
  useAuth(); // Ensure auth context is available
  const [step, setStep] = useState<'shipping' | 'payment' | 'confirmation'>('shipping');
  const [selectedShipping, setSelectedShipping] = useState(shippingOptions[0].id);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ShippingFormData>({
    defaultValues: {
      country: 'US',
    },
  });

  if (items.length === 0) {
    navigate({ to: '/cart' });
    return null;
  }

  const subtotal = getTotalPrice();
  const shippingCost = shippingOptions.find((o) => o.id === selectedShipping)?.price || 0;
  const tax = Math.round(subtotal * 0.08);
  const total = subtotal + shippingCost + tax;

  const onShippingSubmit = (data: ShippingFormData) => {
    console.log('Shipping data:', data);
    setStep('payment');
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    await new Promise((r) => setTimeout(r, 2000));
    setIsProcessing(false);
    setStep('confirmation');
    clearCart();
  };

  if (step === 'confirmation') {
    return (
      <main className="container-page py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center"
        >
          <div className="w-16 h-16 bg-leaf/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-leaf" />
          </div>
          <h1 className="text-3xl font-serif text-warm-900 mb-4">Order Confirmed!</h1>
          <p className="text-warm-600 mb-2">Thank you for your order.</p>
          <p className="text-warm-500 text-sm mb-8">
            Order #KAM-{Date.now().toString(36).toUpperCase()}
          </p>
          <p className="text-warm-600 mb-8">
            You will receive an email confirmation shortly with your order details and tracking
            information.
          </p>
          <Button onClick={() => navigate({ to: '/products' })}>Continue Shopping</Button>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="container-page py-12">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-serif text-warm-900 mb-8">Checkout</h1>

        <div className="flex gap-4 mb-8">
          <StepIndicator
            step={1}
            label="Shipping"
            active={step === 'shipping'}
            completed={step === 'payment'}
          />
          <StepIndicator
            step={2}
            label="Payment"
            active={step === 'payment'}
            completed={false}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {step === 'shipping' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Shipping Address
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit(onShippingSubmit)} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName">First Name</Label>
                          <Input
                            id="firstName"
                            {...register('firstName', { required: 'Required' })}
                            className={errors.firstName ? 'border-red-500' : ''}
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input
                            id="lastName"
                            {...register('lastName', { required: 'Required' })}
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            {...register('email', { required: 'Required' })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Phone</Label>
                          <Input id="phone" type="tel" {...register('phone')} />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="street1">Address</Label>
                        <Input
                          id="street1"
                          {...register('street1', { required: 'Required' })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="street2">Apartment, suite, etc. (optional)</Label>
                        <Input id="street2" {...register('street2')} />
                      </div>

                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="city">City</Label>
                          <Input
                            id="city"
                            {...register('city', { required: 'Required' })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="state">State</Label>
                          <Select
                            onValueChange={(v) =>
                              register('state').onChange({ target: { value: v } })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                            <SelectContent>
                              {US_STATES.map((state) => (
                                <SelectItem key={state.code} value={state.code}>
                                  {state.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="postalCode">ZIP Code</Label>
                          <Input
                            id="postalCode"
                            {...register('postalCode', { required: 'Required' })}
                          />
                        </div>
                      </div>

                      <div className="pt-6 border-t">
                        <h3 className="font-medium text-warm-900 mb-4 flex items-center gap-2">
                          <Truck className="w-5 h-5" />
                          Shipping Method
                        </h3>
                        <div className="space-y-3">
                          {shippingOptions.map((option) => (
                            <label
                              key={option.id}
                              className={`flex items-center justify-between p-4 rounded-sm border cursor-pointer transition-colors ${
                                selectedShipping === option.id
                                  ? 'border-warm-800 bg-warm-50'
                                  : 'border-warm-200 hover:border-warm-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="shipping"
                                  value={option.id}
                                  checked={selectedShipping === option.id}
                                  onChange={() => setSelectedShipping(option.id)}
                                  className="accent-warm-800"
                                />
                                <div>
                                  <p className="font-medium text-warm-900">{option.name}</p>
                                  <p className="text-sm text-warm-500">
                                    {option.days} business days
                                  </p>
                                </div>
                              </div>
                              <span className="font-medium">{formatPrice(option.price)}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <Button type="submit" className="w-full" size="lg">
                        Continue to Payment
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === 'payment' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Payment Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-warm-100 rounded-sm p-6 mb-6 text-center">
                      <p className="text-warm-600 mb-2">
                        Stripe payment integration will appear here
                      </p>
                      <p className="text-sm text-warm-500">
                        For demo purposes, click below to simulate payment
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setStep('shipping')}
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handlePayment}
                        disabled={isProcessing}
                        className="flex-1"
                        size="lg"
                      >
                        {isProcessing ? 'Processing...' : `Pay ${formatPrice(total)}`}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card>
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-6">
                    {items.map((item) => (
                      <div
                        key={`${item.productId}-${item.variantId || ''}`}
                        className="flex gap-3"
                      >
                        <div className="w-16 h-20 rounded-sm overflow-hidden bg-warm-100 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-warm-900 truncate">
                            {item.name}
                          </p>
                          <p className="text-xs text-warm-500">Qty: {item.quantity}</p>
                          <p className="text-sm text-warm-700">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2 pt-4 border-t text-sm">
                    <div className="flex justify-between text-warm-600">
                      <span>Subtotal</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-warm-600">
                      <span>Shipping</span>
                      <span>{formatPrice(shippingCost)}</span>
                    </div>
                    <div className="flex justify-between text-warm-600">
                      <span>Tax</span>
                      <span>{formatPrice(tax)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-medium text-warm-900 pt-2 border-t">
                      <span>Total</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function StepIndicator({
  step,
  label,
  active,
  completed,
}: {
  step: number;
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          active
            ? 'bg-warm-800 text-warm-50'
            : completed
              ? 'bg-leaf text-white'
              : 'bg-warm-200 text-warm-500'
        }`}
      >
        {completed ? <Check className="w-4 h-4" /> : step}
      </div>
      <span className={active ? 'text-warm-900 font-medium' : 'text-warm-500'}>{label}</span>
    </div>
  );
}
